import { useState, useEffect, Suspense } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Users, UserPlus, GraduationCap, Building2,
  BookOpen, Wallet, CalendarCheck2, Plug, LogOut, User2, ChevronRight, Upload, BarChart3, Receipt, MessageCircle, TrendingUp, ClipboardCheck, Activity, Filter, CalendarClock, Mail, CheckSquare, Sparkles, FilePlus2, PhoneCall, Megaphone, Facebook, Send, Target, Workflow, HeartPulse, Users2, Video, Gauge, PlayCircle, Sun, Trophy, Menu, Plus, Loader2,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import FollowupBell from "./FollowupBell";
import { PushBell } from "./PushBell";
import { registerServiceWorker } from "../lib/push";
import QuickActionSheet from "./QuickActionSheet";

const NAV_GROUPS = [
  {
    section: "Main",
    items: [
      { to: "/", icon: LayoutDashboard, label: "Dashboard" },
      { to: "/team-overview", icon: Users2, label: "Team Overview", managerOrAdmin: true },
      { to: "/employees", icon: Users, label: "Employees", adminOnly: true },
    ],
  },
  {
    section: "Lead Hub",
    items: [
      { to: "/lead-hub", icon: Target, label: "Lead Hub", managerOrAdmin: true },
      { to: "/leads", icon: UserPlus, label: "Leads" },
      { to: "/calling-queue", icon: PhoneCall, label: "Calling Queue" },
      { to: "/campaigns", icon: Megaphone, label: "Lead Campaigns", adminOnly: true },
      { to: "/calling-dashboard", icon: PhoneCall, label: "Calling Dashboard", managerOrAdmin: true },
    ],
  },
  {
    section: "People",
    items: [
      { to: "/students", icon: GraduationCap, label: "Students" },
      { to: "/recorded-students", icon: PlayCircle, label: "Recorded Students" },
      { to: "/student-health", icon: HeartPulse, label: "Student Health" },
      { to: "/franchisees", icon: Building2, label: "Franchisees" },
      { to: "/franchisee-cockpit", icon: Gauge, label: "Franchisee Cockpit", gate: (u) => ["admin", "manager", "franchisee_coordinator"].includes(u.role) },
      { to: "/mentorship", icon: GraduationCap, label: "Mentorship", gate: (u) => ["admin", "manager", "franchisee_coordinator"].includes(u.role) },
      { to: "/franchisee-meets", icon: Video, label: "Franchisee Meets" },
      { to: "/franchisee-teams", icon: Users2, label: "Franchisee Teams" },
      { to: "/franchisee-performance", icon: Activity, label: "Franchisee Performance" },
    ],
  },
  {
    section: "Sales",
    items: [
      { to: "/enrollments", icon: FilePlus2, label: "Enrollments" },
      { to: "/courses", icon: BookOpen, label: "Courses" },
      { to: "/incentives", icon: Wallet, label: "Incentives", gate: (u) => u.role === "admin" || u.role === "manager" || !!u?.permissions?.incentives?.manage },
      { to: "/my-incentives", icon: Receipt, label: "My Payslip" },
    ],
  },
  {
    section: "Engagement",
    items: [
      { to: "/retarget", icon: Target, label: "Re-target", gate: (u) => u.role === "admin" || u.role === "manager" || !!u?.permissions?.retarget?.use },
      { to: "/drip", icon: Workflow, label: "Drip / Nurture", managerOrAdmin: true },
      { to: "/email", icon: Send, label: "Email Center" },
      { to: "/whatsapp/inbox", icon: MessageCircle, label: "WhatsApp Inbox" },
      { to: "/segments", icon: Filter, label: "Segments" },
      { to: "/consultations", icon: Sparkles, label: "Consultations" },
    ],
  },
  {
    section: "My Work",
    items: [
      { to: "/my-day", icon: Sun, label: "My Day" },
      { to: "/tasks", icon: CheckSquare, label: "Tasks" },
      { to: "/attendance", icon: CalendarCheck2, label: "Attendance" },
      { to: "/daily-report", icon: ClipboardCheck, label: "Daily Report" },
    ],
  },
  {
    section: "Reports & Analytics",
    items: [
      { to: "/team-performance", icon: Trophy, label: "Team Performance", managerOrAdmin: true },
      { to: "/team-reports", icon: Activity, label: "Team Reports", adminOnly: true },
      { to: "/call-reports", icon: PhoneCall, label: "Call Reports", managerOrAdmin: true },
      { to: "/analytics", icon: BarChart3, label: "Analytics", adminOnly: true },
      { to: "/ad-roi", icon: TrendingUp, label: "Ad ROI", managerOrAdmin: true },
      { to: "/analytics/payment-funnel", icon: TrendingUp, label: "Payment Funnel", managerOrAdmin: true },
    ],
  },
  {
    section: "System",
    items: [
      { to: "/reminders", icon: MessageCircle, label: "Reminders", adminOnly: true },
      { to: "/schedules", icon: CalendarClock, label: "Schedules" },
      { to: "/import", icon: Upload, label: "Import", adminOnly: true },
      { to: "/integrations", icon: Plug, label: "Integrations", adminOnly: true },
      { to: "/facebook-leads", icon: Facebook, label: "Facebook Lead Ads", adminOnly: true },
      { to: "/integrations/whatsapp", icon: MessageCircle, label: "WhatsApp Setup", adminOnly: true },
      { to: "/whatsapp-sending", icon: MessageCircle, label: "WhatsApp Sending", gate: (u) => ["admin", "manager", "franchisee_coordinator"].includes(u.role) },
      { to: "/settings/email", icon: Mail, label: "Email Setup", adminOnly: true },
      { to: "/email-templates", icon: Mail, label: "Email Templates", adminOnly: true },
    ],
  },
];

// Bottom tab bar destinations (mobile) — present for all roles
const BOTTOM_TABS = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", testid: "tab-dashboard" },
  { to: "/leads", icon: UserPlus, label: "Leads", testid: "tab-leads" },
  { to: "/calling-queue", icon: PhoneCall, label: "Calls", testid: "tab-calls" },
  { to: "/my-day", icon: Sun, label: "My Day", testid: "tab-my-day" },
];

function NavGroups({ user, isAdmin, isManagerOrAdmin, onNavigate }) {
  return NAV_GROUPS.map((group) => {
    const visible = group.items.filter((n) => {
      if (n.gate) return n.gate(user);
      if (n.adminOnly) return isAdmin;
      if (n.managerOrAdmin) return isManagerOrAdmin;
      return true;
    });
    if (visible.length === 0) return null;
    return (
      <div key={group.section} className="space-y-1">
        <div className="px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
          {group.section}
        </div>
        {visible.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            onClick={onNavigate}
            data-testid={`nav-${n.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 md:py-2 rounded-md text-sm font-medium transition-colors duration-200 min-h-[44px] md:min-h-0 ${
                isActive
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-foreground/80 hover:bg-muted hover:text-foreground"
              }`
            }
          >
            <n.icon className="h-4 w-4 shrink-0" />
            <span className="truncate">{n.label}</span>
            <ChevronRight className="h-3.5 w-3.5 ml-auto opacity-40" />
          </NavLink>
        ))}
      </div>
    );
  });
}

function ProfileFooter({ user, navigate, logout, onNavigate }) {
  return (
    <div className="border-t border-border p-3">
      <button
        onClick={() => { onNavigate?.(); navigate("/profile"); }}
        data-testid="profile-card-btn"
        className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors min-h-[44px]"
      >
        <Avatar className="h-9 w-9">
          <AvatarImage src={user.photo_url} />
          <AvatarFallback className="bg-secondary text-secondary-foreground font-heading">
            {(user.name || user.email || "U").slice(0, 1).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 text-left overflow-hidden">
          <div className="text-sm font-medium truncate" data-testid="sidebar-username">{user.name}</div>
          <div className="text-xs text-muted-foreground truncate">{user.employee_code || user.role}</div>
        </div>
      </button>
      <Button
        variant="ghost"
        size="sm"
        onClick={async () => { onNavigate?.(); await logout(); navigate("/login"); }}
        data-testid="logout-btn"
        className="w-full mt-2 justify-start text-muted-foreground hover:text-destructive hover:bg-destructive/10 min-h-[44px]"
      >
        <LogOut className="h-4 w-4 mr-2" /> Sign out
      </Button>
    </div>
  );
}

function Brand({ className = "" }) {
  return (
    <div className={className}>
      <div className="font-heading font-bold text-xl tracking-tight text-foreground">
        Pinnacle <span className="text-primary">Vastu</span>
      </div>
      <div className="label-mono mt-1">CRM • HRMS</div>
    </div>
  );
}

function BottomTab({ t }) {
  return (
    <NavLink
      to={t.to}
      end={t.to === "/"}
      data-testid={t.testid}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center gap-1 min-w-[56px] min-h-[44px] transition-colors ${
          isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <t.icon className={`h-5 w-5 ${isActive ? "stroke-[2.5px]" : ""}`} />
          <span className="text-[10px] font-medium tracking-wide">{t.label}</span>
        </>
      )}
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  useEffect(() => { registerServiceWorker(); }, []);
  if (!user) return null;
  const isAdmin = user.role === "admin";
  const isManagerOrAdmin = isAdmin || user.role === "manager";
  const closeDrawer = () => setDrawerOpen(false);

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar (>= md) */}
      <aside className="hidden md:flex w-64 shrink-0 border-r border-border bg-card flex-col" data-testid="sidebar">
        <Brand className="px-6 py-6 border-b border-border" />
        <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
          <NavGroups user={user} isAdmin={isAdmin} isManagerOrAdmin={isManagerOrAdmin} />
        </nav>
        <ProfileFooter user={user} navigate={navigate} logout={logout} />
      </aside>

      {/* Mobile top bar (< md) */}
      <header className="flex md:hidden fixed top-0 left-0 right-0 z-40 h-16 items-center justify-between px-4 border-b border-border bg-background/80 backdrop-blur-xl">
        <button
          onClick={() => setDrawerOpen(true)}
          data-testid="mobile-menu-trigger"
          aria-label="Open menu"
          className="flex items-center justify-center h-11 w-11 -ml-2 rounded-md hover:bg-muted text-foreground"
        >
          <Menu className="h-6 w-6" />
        </button>
        <button onClick={() => navigate("/")} data-testid="mobile-brand-logo" className="font-heading font-bold text-lg tracking-tight">
          Pinnacle <span className="text-primary">Vastu</span>
        </button>
        <div className="flex items-center gap-1">
          <FollowupBell />
          <PushBell />
          <button onClick={() => navigate("/profile")} aria-label="Profile" data-testid="mobile-avatar" className="h-11 w-11 flex items-center justify-center">
            <Avatar className="h-9 w-9">
              <AvatarImage src={user.photo_url} />
              <AvatarFallback className="bg-primary/10 text-primary font-heading">
                {(user.name || "U").slice(0, 1).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </button>
        </div>
      </header>

      {/* Mobile navigation drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-[85vw] max-w-[320px] p-0 flex flex-col bg-background" data-testid="mobile-nav-drawer">
          <SheetTitle className="sr-only">Navigation menu</SheetTitle>
          <Brand className="h-16 flex flex-col justify-center px-6 border-b border-border shrink-0" />
          <nav className="flex-1 overflow-y-auto px-4 py-5 space-y-5">
            <NavGroups user={user} isAdmin={isAdmin} isManagerOrAdmin={isManagerOrAdmin} onNavigate={closeDrawer} />
          </nav>
          <ProfileFooter user={user} navigate={navigate} logout={logout} onNavigate={closeDrawer} />
        </SheetContent>
      </Sheet>

      {/* Main content */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Desktop header (>= md) */}
        <header className="hidden md:flex h-16 border-b border-border bg-card items-center justify-between px-8">
          <div className="font-heading font-semibold text-lg" data-testid="page-greeting">
            Namaste, {user.name?.split(" ")[0] || "User"}
          </div>
          <div className="flex items-center gap-3">
            <FollowupBell />
          <PushBell />
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Logged in as</div>
              <div className="text-sm font-medium capitalize">{user.role}</div>
            </div>
            <Avatar className="h-9 w-9">
              <AvatarImage src={user.photo_url} />
              <AvatarFallback className="bg-primary/10 text-primary font-heading">
                <User2 className="h-4 w-4" />
              </AvatarFallback>
            </Avatar>
          </div>
        </header>
        <div className="flex-1 overflow-auto p-4 pt-20 pb-24 md:p-8">
          <Suspense fallback={<div className="flex items-center justify-center min-h-[50vh]" data-testid="page-loader"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
            <Outlet />
          </Suspense>
        </div>
      </main>

      {/* Mobile bottom tab bar (< md) */}
      <nav className="flex md:hidden fixed bottom-0 left-0 right-0 z-50 h-16 items-center justify-around border-t border-border bg-background px-2 pt-1 shadow-[0_-4px_24px_rgba(0,0,0,0.06)]" data-testid="mobile-bottom-nav">
        {BOTTOM_TABS.slice(0, 2).map((t) => <BottomTab key={t.to} t={t} />)}
        <button
          onClick={() => setQuickOpen(true)}
          data-testid="mobile-quick-action-fab"
          aria-label="Quick actions"
          className="flex items-center justify-center h-14 w-14 -mt-7 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 ring-4 ring-background transition-transform active:scale-95 hover:bg-primary/90"
        >
          <Plus className="h-6 w-6" />
        </button>
        {BOTTOM_TABS.slice(2).map((t) => <BottomTab key={t.to} t={t} />)}
      </nav>

      <QuickActionSheet open={quickOpen} onOpenChange={setQuickOpen} />
    </div>
  );
}
